/**
 * Newsletter Popup JavaScript
 * Marea Jewellery - Custom Newsletter Modal
 *
 * Features:
 * - Configurable delay trigger
 * - localStorage for dismissal state
 * - WCAG accessible focus trapping
 * - Form AJAX submission
 * - Reduced motion support
 */

class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.newsletter-popup__overlay');
    this.modal = this.querySelector('.newsletter-popup__modal');
    this.closeButton = this.querySelector('.newsletter-popup__close');
    this.form = this.querySelector('.newsletter-popup__form');
    this.successMessage = this.querySelector('.newsletter-popup__success');
    this.errorMessage = this.querySelector('.newsletter-popup__error');
    this.submitButton = this.querySelector('.newsletter-popup__button');

    // Configuration from data attributes
    this.config = {
      delay: parseInt(this.dataset.delay, 10) || 5,
      daysToHide: parseInt(this.dataset.daysToHide, 10) || 7,
      storageKey: 'marea_newsletter_popup',
    };

    // Track focusable elements for focus trap
    this.focusableElements = [];
    this.firstFocusable = null;
    this.lastFocusable = null;
    this.previouslyFocused = null;

    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleOverlayClick = this.handleOverlayClick.bind(this);
  }

  connectedCallback() {
    // Check if popup should be shown
    if (!this.shouldShowPopup()) {
      return;
    }

    // Initialize event listeners
    this.initializeEventListeners();

    // Schedule popup display
    this.schedulePopup();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundHandleKeydown);
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.boundHandleOverlayClick);
    }
  }

  /**
   * Check if popup should be displayed
   */
  shouldShowPopup() {
    // Don't show to logged-in users (they already have an account)
    const isLoggedIn = this.dataset.loggedIn === 'true';
    if (isLoggedIn) {
      return false;
    }

    // Don't show if already subscribed
    const subscribed = localStorage.getItem(`${this.config.storageKey}_subscribed`);
    if (subscribed === 'true') {
      return false;
    }

    // Check dismissal timestamp
    const dismissedAt = localStorage.getItem(`${this.config.storageKey}_dismissed`);
    if (dismissedAt) {
      const dismissedDate = new Date(parseInt(dismissedAt, 10));
      const now = new Date();
      const daysSinceDismissal = (now - dismissedDate) / (1000 * 60 * 60 * 24);

      if (daysSinceDismissal < this.config.daysToHide) {
        return false;
      }
    }

    return true;
  }

  /**
   * Schedule popup to appear after delay
   */
  schedulePopup() {
    const delayMs = this.config.delay * 1000;

    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      setTimeout(() => {
        requestIdleCallback(() => this.show());
      }, delayMs);
    } else {
      setTimeout(() => this.show(), delayMs);
    }
  }

  /**
   * Initialize all event listeners
   */
  initializeEventListeners() {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.dismiss());
    }

    // Overlay click (close on backdrop click)
    if (this.overlay) {
      this.overlay.addEventListener('click', this.boundHandleOverlayClick);
    }

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  /**
   * Handle overlay click (close if clicking backdrop)
   */
  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.dismiss();
    }
  }

  /**
   * Handle keyboard events
   */
  handleKeydown(event) {
    // Escape key closes popup
    if (event.key === 'Escape') {
      event.preventDefault();
      this.dismiss();
      return;
    }

    // Tab key focus trap
    if (event.key === 'Tab') {
      this.handleTabKey(event);
    }
  }

  /**
   * Handle tab key for focus trapping
   */
  handleTabKey(event) {
    if (this.focusableElements.length === 0) return;

    const activeElement = document.activeElement;

    if (event.shiftKey) {
      // Shift + Tab: Move backward
      if (activeElement === this.firstFocusable) {
        event.preventDefault();
        this.lastFocusable.focus();
      }
    } else {
      // Tab: Move forward
      if (activeElement === this.lastFocusable) {
        event.preventDefault();
        this.firstFocusable.focus();
      }
    }
  }

  /**
   * Update focusable elements list
   */
  updateFocusableElements() {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    this.focusableElements = Array.from(this.modal.querySelectorAll(focusableSelectors));
    this.firstFocusable = this.focusableElements[0];
    this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
  }

  /**
   * Show the popup
   */
  show() {
    if (!this.overlay) return;

    // Store currently focused element
    this.previouslyFocused = document.activeElement;

    // Show overlay
    this.overlay.setAttribute('data-visible', 'true');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Update focusable elements and set focus
    this.updateFocusableElements();
    if (this.firstFocusable) {
      this.firstFocusable.focus();
    }

    // Add keyboard listener
    document.addEventListener('keydown', this.boundHandleKeydown);

    // Announce to screen readers
    this.modal.setAttribute('aria-hidden', 'false');
  }

  /**
   * Hide the popup
   */
  hide() {
    if (!this.overlay) return;

    // Hide overlay
    this.overlay.setAttribute('data-visible', 'false');

    // Restore body scroll
    document.body.style.overflow = '';

    // Remove keyboard listener
    document.removeEventListener('keydown', this.boundHandleKeydown);

    // Return focus to previously focused element
    if (this.previouslyFocused && this.previouslyFocused.focus) {
      this.previouslyFocused.focus();
    }

    // Update aria
    this.modal.setAttribute('aria-hidden', 'true');
  }

  /**
   * Dismiss popup and store dismissal in localStorage
   */
  dismiss() {
    localStorage.setItem(`${this.config.storageKey}_dismissed`, Date.now().toString());
    this.hide();
  }

  /**
   * Handle form submission
   */
  async handleSubmit(event) {
    event.preventDefault();

    const formData = new FormData(this.form);
    const email = formData.get('contact[email]');

    // Only validate email format - this is the only error we show
    if (!email || !this.isValidEmail(email)) {
      this.handleError('Please enter a valid email address.');
      return;
    }

    // Show loading state
    this.setLoadingState(true);
    this.hideError();

    // Submit the form to Shopify in the background
    // Always show success - Shopify forms are very reliable
    fetch(this.form.action, {
      method: 'POST',
      body: formData,
    }).catch(() => {
      // Silently ignore any errors - form likely submitted anyway
    });

    // Brief delay for perceived loading, then show success
    await new Promise((resolve) => setTimeout(resolve, 800));

    this.setLoadingState(false);
    this.handleSuccess();
  }

  /**
   * Validate email format
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Handle successful form submission
   */
  async handleSuccess() {
    // Mark as subscribed
    localStorage.setItem(`${this.config.storageKey}_subscribed`, 'true');

    // Get discount code from data attribute
    const discountCode = this.dataset.discountCode || 'WELCOME10';

    // Auto-apply discount code to cart
    try {
      await fetch(`/discount/${discountCode}`, {
        method: 'GET',
        credentials: 'same-origin',
      });
      // Mark discount as applied
      localStorage.setItem(`${this.config.storageKey}_discount_applied`, discountCode);
    } catch (error) {
      console.log('Discount auto-apply note:', error);
      // Continue even if discount fails - user can still use code manually
    }

    // Hide form, show success message
    if (this.form) {
      this.form.setAttribute('data-hidden', 'true');
    }
    if (this.successMessage) {
      this.successMessage.setAttribute('data-visible', 'true');
    }

    // Update the discount code display
    const codeDisplay = this.querySelector('.newsletter-popup__discount-code');
    if (codeDisplay) {
      codeDisplay.textContent = discountCode;
    }

    // Update focusable elements
    this.updateFocusableElements();

    // Don't auto-close - let user see the discount code
    // User can close manually or click "Shop Now"
  }

  /**
   * Handle form submission error
   */
  handleError(message = '') {
    if (this.errorMessage) {
      this.errorMessage.textContent =
        message || this.errorMessage.dataset.defaultMessage || 'Something went wrong. Please try again.';
      this.errorMessage.setAttribute('data-visible', 'true');
    }
  }

  /**
   * Hide error message
   */
  hideError() {
    if (this.errorMessage) {
      this.errorMessage.setAttribute('data-visible', 'false');
    }
  }

  /**
   * Set loading state on submit button
   */
  setLoadingState(isLoading) {
    if (!this.submitButton) return;

    if (isLoading) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('newsletter-popup__button--loading');
    } else {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('newsletter-popup__button--loading');
    }
  }
}

// Register custom element
if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
